// Frontend UI translations (labels, buttons, section titles).
// Content (cards, quiz questions) comes from backend to allow future CMS.
export const translations = {
  it: {
    app: {
      name: "ExchangePlan",
      payoff: "Il tuo periodo di studio all'estero, un passo alla volta.",
    },
    nav: {
      login: "Accedi",
      logout: "Esci",
      dashboard: "Bacheca",
      faq: "FAQ",
    },
    landing: {
      title: "A che punto sei?",
      subtitle: "Scegli da dove parti. Da lì, ti accompagniamo.",
      intro: "Che tu stia ancora valutando l'idea di un periodo di studio all'estero o abbia già scelto tutto, qui trovi contenuti, strumenti e una checklist pensati per ogni fase del tuo percorso — senza fretta e senza pressioni.",
      parentNote: "🎓 L'esplorazione iniziale è pensata per te. La parte organizzativa, più avanti, si fa insieme a un genitore.",
      choices: {
        exploring: {
          title: "Non so se voglio partire",
          desc: "Sono ancora indeciso, sto solo esplorando l'idea.",
        },
        deciding: {
          title: "Ho deciso, ma devo capire come",
          desc: "Ho deciso di partire, ora scelgo dove, quanto e con chi.",
        },
        organizing: {
          title: "Ho scelto, ora organizzo",
          desc: "Ora gestisci documenti e partenza.",
        },
        aboard: {
          title: "Sono già partito",
          desc: "Sono già in viaggio, sto vivendo l'esperienza.",
        },
        returned: {
          title: "Sono rientrato",
          desc: "Sono rientrato, ora sistemo tutto il resto.",
        },
      },
      soonBanner: "La fase \"Al rientro\" arriva presto. Ti portiamo comunque nella tua bacheca personale.",
    },
    phase: {
      plan: "Organizza la partenza",
      checkin: "Check-in",
      aboard: "Negli USA",
      landing: "Al rientro",
      progressLabel: (pct) => `${pct}% completato`,
      progressCount: (done, total) => `${done} di ${total} completati`,
      locked: "Presto disponibile",
      soonBadge: "presto",
      goToPhase: "Vai alla fase",
      // Abbreviated labels for the compact mobile stepper (390px viewport)
      // where the full name of Phase 1 wraps awkwardly to 2 lines.
      shortPlan: "Organizza",
      shortCheckin: "Check-in",
      shortAboard: "Negli USA",
      shortLanding: "Al rientro",
    },
    checkin: {
      title: "Check-in",
      subtitle: "Ultimi controlli prima di salire a bordo.",
      // Shortened disclaimer scoped to Check-in. "Imposta le tue date..." is
      // intentionally omitted because this page has no per-step date picker
      // (unlike /organizza), so the sentence wouldn't apply here.
      timingDisclaimerShort: "Le informazioni sono indicative: ogni scuola e agenzia ha le sue tempistiche. Manca qualcosa?",
      placeholderTitle: "Presto qui: check-in pre-partenza",
      placeholderBody: "Stiamo lavorando alla checklist di cose da portare con te (documenti in cabina, adattatori, kit di emergenza, contatti chiave). Torna a breve.",
      backToPlan: "Torna a Organizza la partenza",
      backToCheckin: "Torna a Check-in",
      toolsIntro: "Tre strumenti per gli ultimi metri prima della partenza. Ogni progresso viene salvato in automatico.",
      toolPackingTitle: "Lista valigia",
      toolPackingDesc: "Checklist personalizzata sulla zona climatica di destinazione.",
      toolBudgetTitle: "Budget tracker pre-partenza",
      toolBudgetDesc: "Traccia le spese fisse da sostenere prima della partenza.",
      toolDeparturePartTitle: "Check documenti partenza",
      toolDeparturePartDesc: "L'ultimo controllo snello, per gli ultimissimi giorni.",
      toolGiftsDesc: "Perché portarli e cosa mettere in valigia — poi la tua lista.",
      toolConnectivityDesc: "Come pagare e restare connesso dai primi giorni.",
      climateZoneQuestion: "In quale zona climatica ti troverai?",
      climateZoneHint: "Se non hai ancora ricevuto l'abbinamento, scegli 'Non lo so ancora' — mostreremo una lista completa.",
      climateChangeCta: "Cambia zona climatica",
      packingProgress: (done, total) => `${done} di ${total} preparate`,
      addRow: "Aggiungi voce",
      removeRow: "Rimuovi",
      customLabelPh: "Descrizione voce",
      customBadge: "Personalizzata",
      amountPh: "Importo (€)",
      total: "Totale",
      documentsCounter: (done, total) => `${done} di ${total} pronti`,
      savedIndicator: "Salvato",
      savingIndicator: "Salvataggio…",
      giftsListTitle: "Le tue idee regalo",
      giftsListEmpty: "Nessuna idea ancora. Aggiungine una qui sotto.",
      labelWhy: "Perché",
      labelHow: "Cosa fare",
      labelWarning: "Attenzione",
    },
    abordo: {
      title: "Negli USA",
      subtitle: "Il percorso una volta arrivato negli USA. Alcune sezioni sono ancora in preparazione.",
    },
    faq: {
      title: "Domande frequenti",
      subtitle: "Le risposte alle domande che ricorrono più spesso lungo il percorso.",
      searchPlaceholder: "Cerca nelle FAQ…",
      filterAll: "Tutte",
      noResults: "Nessuna FAQ trovata per questa ricerca.",
    },
    planCompleted: {
      title: "Hai completato tutta la fase \"Organizza la partenza\"!",
      body: "Vuoi passare a \"Check-in\"?",
      goCheckin: "Sì, vai a Check-in",
      stayHere: "Non ancora, resto qui",
    },
    modes: {
      explorativa: {
        tab: "Esplora",
        title: "Lo sai che...",
        subtitle: "Prenditi tempo. Sfoglia le card e scopri cosa significa davvero vivere un'esperienza da exchange student",
        decks: {
          practical: "Pratiche",
          emotional: "Emotive",
        },
        reflectCta: "Pronto a riflettere?",
        reflectDisabled: "Sfoglia ancora un po' prima di partire con il quiz",
        skipNow: "Salta subito al quiz",
        seenCount: (n, tot) => `${n}/${tot} card viste`,
        learnMore: "Approfondisci",
        showLess: "Chiudi approfondimento",
        share: "Condividi",
        shareTitle: "Una card di ExchangePlan",
        shareText: "Da ExchangePlan — l'app che ti accompagna nel tuo periodo di studio all'estero.",
        exit: "Esci",
      },
      confronto: {
        tab: "Confronta",
        title: "Cosa conta per te",
        subtitle: "Scegli un paese e leggi cosa vale la pena considerare prima di scegliere.",
        soon: "Presto disponibile",
        continueCta: "Ho scelto, ora organizzo",
      },
      organizzativa: {
        tab: "Organizza",
        title: "Cosa fare prima di partire per l'exchange negli USA.",
        subtitle: "Scuola, agenzia, visto e documenti: ogni passaggio con la sua scadenza indicativa. Leggi tutto liberamente; con l'account gratuito spunti i passaggi e salvi le tue date.",
        organizzaDisclaimer: "Le scadenze sono indicative: ogni scuola e agenzia ha le sue tempistiche. Imposta le tue date su ogni passaggio. Manca qualcosa?",
        progressCounter: (done, total) => `${done} di ${total} passaggi`,
        progressTotal: (total) => `${total} passaggi`,
        statusAll: "Tutte",
        statusTodo: "Da fare",
        statusDone: "Fatte",
        statusFilterAria: "Filtra per stato",
        statusEmptyTodo: "Hai completato tutti i passi, ottimo lavoro!",
        statusEmptyDone: "Non hai ancora completato nessun passo.",
        savePromptTitle: "Salva i tuoi progressi",
        savePromptBody: "Crea un account gratuito per spuntare i passaggi, salvare le tue date e ritrovarle quando torni.",
        savePromptCta: "Crea l'account gratis",
      loginParentTitle: "Un passaggio insieme a un genitore",
      loginParentBody: "Da qui in poi la parte organizzativa — documenti, scadenze, pratiche — è più facile da gestire insieme a un genitore. Accedi con l'account che userete per seguire l'organizzazione.",
      loginRequired: "Accedi per organizzare le tue scadenze",
        profileTitle: "Profilo partenza",
        profileSubtitle: "Alcuni dati chiave per personalizzare la tua checklist.",
        profileOrigin: "Paese di origine",
        profileHomeSchool: "Scuola di partenza",
        profileHomeSchoolPh: "Es. Liceo Classico Manzoni, Milano",
        profileStudyTrack: "Indirizzo di studio",
        profileStudyTrackPh: "Seleziona un indirizzo",
        profileStudyTrackOther: "Specifica l'indirizzo",
        profileDestination: "Paese di destinazione",
        profileDestinationPh: "Seleziona un paese",
        profileAgency: "Agenzia scelta",
        profileAgencyPh: "Seleziona un'agenzia",
        profileAgencyOther: "Specifica il nome dell'agenzia",
        profileAgencyOtherDisplay: "Altra agenzia",
        profileYearAbroad: "Anno scolastico all'estero",
        profileYearAbroadPh: "Seleziona l'anno",
        profileDuration: "Durata del programma",
        profileDurationPh: "Seleziona la durata",
        profileItalianYear: "Anno scolastico italiano corrispondente",
        profileItalianYearPh: "Seleziona l'anno",
        italianYearThird: "III superiore",
        italianYearFourth: "IV superiore",
        italianYearFifth: "V superiore",
        unsupportedComboTitle: "Combinazione non ancora supportata",
        unsupportedComboBody: "Ci dispiace, stiamo lavorando per rendere disponibile presto anche questa combinazione. Per ora il sistema supporta solo il percorso Italia → Stati Uniti.",
        unsupportedComboClose: "Chiudi",
        profileDiplomaGoal: "Obiettivo diploma",
        profileDiplomaUS: "Diploma USA a fine anno",
        profileDiplomaIT: "Rientro con diploma italiano",
        durationTrimester: "Trimestre",
        durationSemester: "Semestre",
        durationFullYear: "Anno intero",
        profileSave: "Salva e prosegui",
        profileEdit: "Modifica profilo",
        personalizeProfileCta: "Personalizza il tuo profilo",
        personalizeProfileHint: "Opzionale — aggiungi scuola, anno e obiettivo per adattare la checklist alla tua situazione.",
        profilePanelClose: "Chiudi",
        profileSavePartial: "Salva profilo",
        profileClearField: "Azzera scelta",
        unsupportedTitle: "Presto disponibile",
        unsupportedBody: "Al momento l'MVP supporta solo la combinazione Italia → Stati Uniti. Stiamo lavorando per aggiungere altre destinazioni presto.",
        checklistTitle: "Organizza la partenza",
        sectionDocsTitle: "Documenti e scadenze",
        sectionVaxTitle: "Vaccinazioni",
        sectionSchoolTitle: "Programma scolastico",
        vaxIntroLabel: "Nota importante",
        addCustomItem: "Aggiungi voce",
        customBadge: "Personalizzata",
        customFormTitleCreate: "Aggiungi una voce",
        customFormTitleEdit: "Modifica voce",
        customFieldTitle: "Titolo",
        customFieldTitlePh: "Es. Ritiro certificato traduzione",
        customFieldDeadline: "Scadenza",
        customFieldWhy: "Perché è importante (opzionale)",
        customFieldWhyPh: "A cosa serve nel percorso complessivo",
        customFieldHow: "Cosa fare (opzionale)",
        customFieldHowPh: "Passaggi pratici, dove reperirlo",
        customFieldWarning: "Attenzione (opzionale)",
        customFieldWarningPh: "Errori comuni, cose a cui prestare attenzione",
        customFormSave: "Salva voce",
        customFormEditAction: "Modifica",
        customFormDeleteAction: "Elimina",
        customFormDeleteConfirm: "Eliminare questa voce personalizzata?",
        customTitleRequired: "Il titolo è obbligatorio",
        checklistCounter: (done, total) => `${done} di ${total} ${total === 1 ? "completata" : "completate"}`,
        checklistSortLabel: "Ordina per",
        checklistSortAsc: "Scadenza più vicina",
        checklistSortDesc: "Scadenza più lontana",
        checklistDetailsWhy: "Perché",
        checklistDetailsHow: "Cosa fare",
        checklistDetailsWarning: "Attenzione",
        checklistLearnMore: "Approfondisci",
        checklistShowLess: "Chiudi approfondimento",
        subtasksTitle: "Sotto-task",
        subtaskAdd: "Aggiungi sotto-task",
        systemBadge: "Suggerita",
        filterAll: "Tutti",
        catDecision: "Decisione",
        catSchoolIt: "Scuola IT",
        catAgency: "Agenzia",
        catVisa: "Pratiche",
        catSchoolUs: "Scuola USA",
        catHosting: "Accoglienza",
        // Legacy category keys kept for backwards compatibility with older data
        catDocuments: "Documenti",
        catVaccinations: "Vaccinazioni",
        catSchool: "Scuola",
        unifiedTitle: "Cose da fare",
        deadlineDisclaimer: "Le indicazioni temporali (es. \"18-15 mesi prima\") sono puramente indicative e non vincolanti — ogni percorso ha i suoi tempi.",
        selectionPh: "Seleziona un'opzione",
        userInputPh: "Inserisci un valore",
        userInputSave: "Salva",
        linkedProfileLabel: "Dal tuo profilo:",
        linkedProfileEdit: "Modifica nel profilo",
        // legacy - kept for backwards compat, no longer rendered:
        deepReadTitle: "Approfondimenti: sistema scolastico USA",
        subtaskTitlePh: "Titolo del sotto-task",
        subtaskNotePh: "Note personali (opzionali)",
        subtaskExplanationLabel: "Come farla",
        subtaskDeadline: "Scadenza",
        subtaskDelete: "Elimina",
        subtaskSave: "Salva",
        subtaskCancel: "Annulla",
        nextActionLabel: "Prossima azione:",
        addDeadline: "Imposta scadenza",
        removeDeadline: "Rimuovi",
        deadline: "Scadenza",
        daysBefore: "gg prima",
      },
    },
    quiz: {
      title: "Un momento per te",
      subtitle: "10 domande. Nessun voto, nessun giudizio.",
      introBody: "Ora ti facciamo 10 domande sul tuo modo di sentire la partenza. Nessuna risposta è giusta o sbagliata: rispondi di pancia, quello che ti viene naturalmente. Puoi fermarti quando vuoi — le tue risposte restano salvate.",
      introStart: "Comincio",
      exit: "Esci per ora",
      progress: (n, tot) => `Domanda ${n} di ${tot}`,
      submitting: "Analizziamo le tue risposte...",
      profileTitle: "Il tuo profilo",
      backHome: "Torna alla bacheca",
      previewOrganize: "Nel frattempo, puoi dare un'occhiata a come si organizza la partenza →",
    },
    talkTo: {
      eyebrow: "Un passo alla volta",
      title: "Con chi ha senso parlarne",
      intro: "Prima di prendere impegni, mettere in parola quello che senti aiuta più di quanto sembri. Non serve trovare qualcuno che abbia tutte le risposte — serve trovare qualcuno che sappia ascoltarti senza fretta.",
      peopleTitle: "Chi ha senso coinvolgere",
      people: [
        { title: "I tuoi genitori", body: "Non per chiedere permesso, ma per portare in famiglia quello che stai iniziando a immaginare. Anche se hanno dubbi, spesso proprio da lì nasce il primo confronto onesto." },
        { title: "Un fratello o una sorella più grande", body: "Chi ti conosce da vicino ma non è un genitore riesce spesso a leggere le tue esitazioni con meno pressione, e a dirti cose che a mamma e papà non diresti mai." },
        { title: "Un insegnante di fiducia", body: "Un prof con cui ti trovi bene ha visto passare tante versioni di te durante gli anni: ha una prospettiva sul tuo percorso che nessun altro ha." },
        { title: "Qualcuno che ha già vissuto uno scambio", body: "Non è la stessa cosa che chiedere a chi \"immagina come sarà\". Chi c'è già stato racconta anche le parti scomode senza edulcorarle, e questo aiuta davvero." },
      ],
      openersTitle: "Come iniziare la conversazione",
      openersHint: "Non sono frasi da leggere a memoria — solo esempi di come mettere in parola qualcosa che magari, dentro di te, non ha ancora una forma chiara.",
      openers: [
        "Ci sto pensando ma non sono sicuro/a. Ti va se ne parliamo un po', anche solo per capire cosa mi preoccupa?",
        "Vorrei provare a spiegarti cosa mi frena, ma prima ho bisogno che tu mi ascolti senza rispondere subito. Va bene?",
        "Ho fatto un quiz che mi ha detto che ho ancora dubbi, ed è vero. Posso raccontarti quali?",
        "Non ho ancora deciso niente. Voglio solo capire cosa penso davvero, e mi aiuterebbe farlo a voce con te.",
      ],
      backToCardsTitle: "Le card come base per la conversazione",
      backToCardsBody: "Se vuoi, rileggi le card \"Lo sai che...\" e portane una o due a chi vuoi parlarne. Sono spunti concreti — un punto da cui partire, invece che una pagina bianca.",
      backToCardsCta: "Rivedi le card",
      exitCta: "Chiudi",
    },
    dashboard: {
      welcome: (name) => `Ciao ${name}`,
      currentPhase: "La tua fase attuale",
      quickActions: "Azioni rapide",
      reminders: "Prossime scadenze",
      noReminders: "Nessuna scadenza attiva. Aggiungi date alla tua checklist.",
      viewLatestQuiz: "Vedi il tuo profilo decisionale",
      goToPlan: "Vai a Organizza la partenza",
      logout: "Esci",
      checklistSummaryTitle: "Checklist documenti",
      checklistSummaryCTA: "Vai alla checklist",
      checklistSummaryUpcoming: "Scadenze imminenti",
      organizeSectionTitle: "Organizza la partenza",
      organizeDocsTitle: "Documenti e scadenze",
      organizeVaxTitle: "Vaccinazioni",
      organizeSchoolTitle: "Programma scolastico",
      organizeSchoolCta: "Vai al programma scolastico",
      organizeSchoolHint: "Contenuto di riflessione, senza scadenze",
      checkinSectionTitle: "Check-in",
      checkinPackingTitle: "Lista valigia",
      checkinPackingEmpty: "Scegli la zona climatica per iniziare",
      checkinDepartureTitle: "Check documenti partenza",
      viewAll: "Vedi tutte",
      decisionProfileTitle: "Il tuo profilo decisionale",
      decisionProfileBody: "Rivedi la sintesi del quiz quando vuoi, o ricomincia daccapo se le tue idee sono cambiate.",
      retakeQuiz: "Rifai il quiz",
      reviewCards: "Rivedi le card",
      daysAgo: (n) => `${n} gg fa`,
      daysLeft: (n) => `${n} gg`,
      // New Bacheca copy (Prossime scadenze moved to the top)
      remindersCompactCounter: (done, total, overdue) =>
        overdue > 0
          ? `${done} di ${total} passi completati · ${overdue} in ritardo`
          : `${done} di ${total} passi completati`,
      remindersOverdueBadge: "In ritardo",
      departureDateMissingTitle: "Imposta la data di partenza",
      departureDateMissingBody:
        "Per vedere le prossime scadenze, imposta l'anno scolastico all'estero nel tuo profilo di partenza.",
      departureDateMissingCta: "Modifica profilo",
      budgetTitle: "Budget totale programma",
      budgetSubtitle: "Somma spese fisse + una tantum in USA",
      budgetEmptyMessage: "Budget non ancora calcolato",
      budgetEmptyCta: "Inizia a compilarlo",
      budgetOpenCta: "Modifica budget",
    },
    common: {
      continue: "Continua",
      back: "Indietro",
      language: "Lingua",
      save: "Salva",
      cancel: "Annulla",
      loading: "Caricamento...",
      soon: "Presto disponibile",
      timingDisclaimer: "Le scadenze e le informazioni che vedi qui sono indicative, pensate per darti un'idea generale di quando muoverti — non sono garantite né esaustive. Ogni programma, agenzia e scuola ha le sue tempistiche specifiche: usa il selettore data per adattarle al tuo percorso reale, e i contenuti di approfondimento per farti un'idea più completa, non come sostituto delle informazioni ufficiali della tua agenzia o scuola. Se qualcosa manca o non coincide con la tua situazione, fallo presente — l'app cresce anche grazie a chi la usa.",
      supportEmail: "exchangeplan.supporto@gmail.com",
      supportInlineCta: "Segnala qui",
      supportInlineFull: "Scrivici a exchangeplan.supporto@gmail.com",
      faqNotFoundLead: "Non hai trovato quello che cercavi?",
      dashboardSupportLead: "Hai un dubbio o hai notato qualcosa che non funziona?",
      supportWriteUs: "Scrivici a",
      authPromptTitle: "Salviamo il tuo lavoro?",
      authPromptBody: "Per salvare i tuoi progressi e ritrovarli quando torni, serve un account — richiede solo pochi secondi. Da qui in poi conviene procedere insieme a un genitore, che potrà gestire con te la parte organizzativa.",
      authPromptSignin: "Accedi con Google",
      authPromptLater: "Continua a esplorare",
      authReasonToggleTask: "Per spuntare le attività e salvarle serve un account.",
      authReasonSetDeadline: "Per impostare una data e salvarla serve un account.",
      authReasonAddCustom: "Per aggiungere un'attività personalizzata serve un account.",
      authReasonEditCustom: "Per modificare un'attività personalizzata serve un account.",
      authReasonDeleteCustom: "Per eliminare un'attività personalizzata serve un account.",
      authReasonSaveProfile: "Per salvare il profilo di partenza serve un account.",
      authReasonSelectOption: "Per selezionare un'opzione e salvarla serve un account.",
      authReasonSaveInput: "Per salvare il valore inserito serve un account.",
      authReasonSaveCheckin: "Per salvare le tue scelte del Check-in serve un account.",
      anonBannerText: "Consigliato: procedere con un genitore.",
      anonBannerDismiss: "Nascondi",
      soonPhaseTitle: "Presto disponibile",
      soonPhaseBody: "Stiamo lavorando per rendere questa fase disponibile a breve. Nel frattempo, se vuoi, esplora le altre sezioni già pronte.",
      soonPhaseCta: "Torna alla home",
    },
    pianoExcel: {
      title: "Piano di partenza: il file Excel gratuito per organizzare il tuo exchange negli USA",
      subtitle: "Un passo alla volta, con le scadenze calcolate dalla tua data di partenza.",
      bullet1: "30 passi dalla decisione iniziale alla scuola americana",
      bullet2: "Inserisci la data di partenza e le scadenze si calcolano da sole",
      bullet3: "Spunta quello che hai fatto e vedi a che punto sei",
      downloadBtn: "Scarica il file Excel",
      fallbackLink: "Se il download non parte, apri qui",
      noticeBold: "Le scadenze sono indicative:",
      noticeRest: " verifica sempre date e requisiti con la tua agenzia e la tua scuola.",
      appCardTitle: "Preferisci farlo online?",
      appCardBody: "Nell'app gratuita trovi lo stesso piano con le scadenze, i passi da spuntare e tutto in un unico posto.",
      appCardCta: "Vai all'app gratuita",
    },
    footer: {
      pianoExcelLink: "Piano Excel gratuito",
    },
  },
  en: {
    app: {
      name: "ExchangePlan",
      payoff: "Your study abroad, one step at a time.",
    },
    nav: {
      login: "Sign in",
      logout: "Sign out",
      dashboard: "Dashboard",
      faq: "FAQ",
    },
    landing: {
      title: "Where are you at?",
      subtitle: "Pick a starting point. We'll take you exactly there.",
      intro: "Whether you're still weighing the idea of a study period abroad or you've already picked everything, here you'll find content, tools and a checklist designed for every step of your path — without rush and without pressure.",
      parentNote: "🎓 The early exploration is designed for you. The organizational side, later on, is handled with a parent.",
      choices: {
        exploring: {
          title: "I'm not sure I want to go",
          desc: "I'm still undecided, I'm only exploring the idea.",
        },
        deciding: {
          title: "I've decided, now figuring out how",
          desc: "I've decided to go, now I'm picking where, how long, and with whom.",
        },
        organizing: {
          title: "I've chosen, now I organize",
          desc: "Now handle documents and departure.",
        },
        aboard: {
          title: "I've already left",
          desc: "I'm already there, I'm living the experience.",
        },
        returned: {
          title: "I'm back home",
          desc: "I'm back, I'm sorting out everything else.",
        },
      },
      soonBanner: "The \"Back home\" phase is coming soon. For now, we take you to your personal dashboard.",
    },
    phase: {
      plan: "Organize your departure",
      checkin: "Check-in",
      aboard: "In the USA",
      landing: "Back home",
      progressLabel: (pct) => `${pct}% complete`,
      progressCount: (done, total) => `${done} of ${total} completed`,
      locked: "Coming soon",
      soonBadge: "soon",
      goToPhase: "Go to phase",
      // Short labels for the compact mobile stepper to prevent awkward
      // wrapping on narrow viewports.
      shortPlan: "Organize",
      shortCheckin: "Check-in",
      shortAboard: "In the USA",
      shortLanding: "Back home",
    },
    checkin: {
      title: "Check-in",
      subtitle: "Final checks before boarding.",
      // Shortened Check-in disclaimer (no date-picker step on this page).
      timingDisclaimerShort: "Information is indicative: every school and agency has its own timing. Something missing?",
      placeholderTitle: "Coming here: pre-departure check-in",
      placeholderBody: "We're building your take-with-you checklist (in-cabin documents, adapters, emergency kit, key contacts). Come back soon.",
      backToPlan: "Back to Organize your departure",
      backToCheckin: "Back to Check-in",
      toolsIntro: "Three tools for the final stretch before departure. Progress is saved automatically.",
      toolPackingTitle: "Packing list",
      toolPackingDesc: "Checklist tailored to your destination's climate zone.",
      toolBudgetTitle: "Pre-departure budget tracker",
      toolBudgetDesc: "Track the fixed expenses to cover before leaving.",
      toolDeparturePartTitle: "Departure documents check",
      toolDeparturePartDesc: "The last lean check, for the very final days.",
      toolGiftsDesc: "Why bring them and what to pack — then your own list.",
      toolConnectivityDesc: "How to pay and stay connected from day one.",
      climateZoneQuestion: "Which climate zone will you be in?",
      climateZoneHint: "If you haven't received the match yet, pick \"I don't know yet\" — we'll show the full list.",
      climateChangeCta: "Change climate zone",
      packingProgress: (done, total) => `${done} of ${total} packed`,
      addRow: "Add row",
      removeRow: "Remove",
      customLabelPh: "Item description",
      customBadge: "Custom",
      amountPh: "Amount (€)",
      total: "Total",
      documentsCounter: (done, total) => `${done} of ${total} ready`,
      savedIndicator: "Saved",
      savingIndicator: "Saving…",
      giftsListTitle: "Your gift ideas",
      giftsListEmpty: "No ideas yet. Add one below.",
      labelWhy: "Why",
      labelHow: "What to do",
      labelWarning: "Watch out",
    },
    abordo: {
      title: "In the USA",
      subtitle: "Your journey once you land in the US. Some sections are still being prepared.",
    },
    faq: {
      title: "Frequently asked questions",
      subtitle: "Answers to the questions that come up most often along the journey.",
      searchPlaceholder: "Search the FAQ…",
      filterAll: "All",
      noResults: "No FAQ found for this search.",
    },
    planCompleted: {
      title: "You've completed the whole \"Organize your departure\" phase!",
      body: "Want to move on to \"Check-in\"?",
      goCheckin: "Yes, go to Check-in",
      stayHere: "Not yet, I'll stay here",
    },
    modes: {
      explorativa: {
        tab: "Explore",
        title: "Did you know...",
        subtitle: "Take your time. Flip through the cards and see what resonates.",
        decks: { practical: "Practical", emotional: "Emotional" },
        reflectCta: "Ready to reflect?",
        reflectDisabled: "Flip through a bit more before starting the quiz",
        skipNow: "Skip to the quiz",
        seenCount: (n, tot) => `${n}/${tot} cards seen`,
        learnMore: "Learn more",
        showLess: "Close details",
        share: "Share",
        shareTitle: "An ExchangePlan card",
        shareText: "From ExchangePlan — the app that walks you through your study-abroad journey.",
        exit: "Exit",
      },
      confronto: {
        tab: "Compare",
        title: "What matters to you",
        subtitle: "Pick a country and read what's worth considering before choosing.",
        soon: "Coming soon",
        continueCta: "I've chosen, now organize",
      },
      organizzativa: {
        tab: "Organize",
        title: "What to do before leaving for your exchange in the USA.",
        subtitle: "School, agency, visa and documents: every step with its indicative deadline. Read everything freely; with the free account you tick off steps and save your dates.",
        organizzaDisclaimer: "Deadlines are indicative: every school and agency has its own timing. Set your own dates on each step. Something missing?",
        progressCounter: (done, total) => `${done} of ${total} steps`,
        progressTotal: (total) => `${total} steps`,
        statusAll: "All",
        statusTodo: "To do",
        statusDone: "Done",
        statusFilterAria: "Filter by status",
        statusEmptyTodo: "You've completed every step. Great job!",
        statusEmptyDone: "You haven't completed any step yet.",
        savePromptTitle: "Save your progress",
        savePromptBody: "Create a free account to tick off steps, save your dates and pick up where you left off.",
        savePromptCta: "Create free account",
      loginParentTitle: "A step to take with a parent",
      loginParentBody: "From here on, the organizational side — documents, deadlines, paperwork — is easier to handle together with a parent. Sign in with the account you'll both use to follow the process.",
      loginRequired: "Sign in to organize your deadlines",
        profileTitle: "Departure profile",
        profileSubtitle: "A few key details to personalize your checklist.",
        profileOrigin: "Country of origin",
        profileHomeSchool: "School name",
        profileHomeSchoolPh: "e.g. Manzoni High School, Milan",
        profileStudyTrack: "Study track",
        profileStudyTrackPh: "Select a track",
        profileStudyTrackOther: "Specify the track",
        profileAgencyOther: "Specify the agency name",
        italianYearThird: "3rd year",
        italianYearFourth: "4th year",
        italianYearFifth: "5th year",
        unsupportedComboTitle: "Combination not supported yet",
        unsupportedComboBody: "Sorry, we're working to make this combination available soon. For now the system only supports the Italy → United States path.",
        unsupportedComboClose: "Close",
        profileDestination: "Destination country",
        profileDestinationPh: "Select a country",
        profileAgency: "Chosen agency",
        profileAgencyPh: "Select an agency",
        profileAgencyOtherDisplay: "Other agency",
        profileYearAbroad: "School year abroad",
        profileYearAbroadPh: "Select year",
        profileDuration: "Program duration",
        profileDurationPh: "Select duration",
        profileItalianYear: "Corresponding Italian school year",
        profileItalianYearPh: "Select year",
        profileDiplomaGoal: "Diploma goal",
        profileDiplomaUS: "US diploma at end of year",
        profileDiplomaIT: "Return with Italian diploma",
        durationTrimester: "Trimester",
        durationSemester: "Semester",
        durationFullYear: "Full year",
        profileSave: "Save and continue",
        profileEdit: "Edit profile",
        personalizeProfileCta: "Personalize your profile",
        personalizeProfileHint: "Optional — add school, year and goal to tailor the checklist to your situation.",
        profilePanelClose: "Close",
        profileSavePartial: "Save profile",
        profileClearField: "Clear selection",
        unsupportedTitle: "Coming soon",
        unsupportedBody: "The MVP currently supports only the Italy → United States combination. We're working to add more destinations soon.",
        checklistTitle: "Organize your departure",
        sectionDocsTitle: "Documents and deadlines",
        sectionVaxTitle: "Vaccinations",
        sectionSchoolTitle: "School program",
        vaxIntroLabel: "Important note",
        addCustomItem: "Add item",
        customBadge: "Custom",
        customFormTitleCreate: "Add an item",
        customFormTitleEdit: "Edit item",
        customFieldTitle: "Title",
        customFieldTitlePh: "e.g. Pick up sworn translation certificate",
        customFieldDeadline: "Deadline",
        customFieldWhy: "Why it matters (optional)",
        customFieldWhyPh: "What role it plays in the overall path",
        customFieldHow: "What to do (optional)",
        customFieldHowPh: "Practical steps, where to get it",
        customFieldWarning: "Watch out (optional)",
        customFieldWarningPh: "Common mistakes, things to look out for",
        customFormSave: "Save item",
        customFormEditAction: "Edit",
        customFormDeleteAction: "Delete",
        customFormDeleteConfirm: "Delete this custom item?",
        customTitleRequired: "Title is required",
        checklistCounter: (done, total) => `${done} of ${total} complete`,
        checklistSortLabel: "Sort by",
        checklistSortAsc: "Nearest deadline",
        checklistSortDesc: "Furthest deadline",
        checklistDetailsWhy: "Why",
        checklistDetailsHow: "What to do",
        checklistDetailsWarning: "Watch out",
        checklistLearnMore: "Learn more",
        checklistShowLess: "Close details",
        subtasksTitle: "Sub-tasks",
        subtaskAdd: "Add sub-task",
        systemBadge: "Suggested",
        filterAll: "All",
        catDecision: "Decision",
        catSchoolIt: "IT school",
        catAgency: "Agency",
        catVisa: "Paperwork",
        catSchoolUs: "US school",
        catHosting: "Hosting",
        // Legacy category keys kept for backwards compatibility with older data
        catDocuments: "Documents",
        catVaccinations: "Vaccinations",
        catSchool: "School",
        unifiedTitle: "Things to do",
        deadlineDisclaimer: "Timing hints (e.g. \"18-15 months ahead\") are just indicative and not binding — every path has its own tempo.",
        selectionPh: "Choose an option",
        userInputPh: "Enter a value",
        userInputSave: "Save",
        linkedProfileLabel: "From your profile:",
        linkedProfileEdit: "Edit in profile",
        deepReadTitle: "Read more: US school system",
        subtaskTitlePh: "Sub-task title",
        subtaskNotePh: "Personal notes (optional)",
        subtaskExplanationLabel: "How to do it",
        subtaskDeadline: "Deadline",
        subtaskDelete: "Delete",
        subtaskSave: "Save",
        subtaskCancel: "Cancel",
        nextActionLabel: "Next action:",
        addDeadline: "Set deadline",
        removeDeadline: "Remove",
        deadline: "Deadline",
        daysBefore: "days before",
      },
    },
    quiz: {
      title: "A moment with yourself",
      subtitle: "10 questions. No score, no judgment.",
      introBody: "We'll ask you 10 questions about how you feel about leaving. There's no right or wrong answer — just answer from the gut, what feels natural. You can stop whenever — your answers are saved.",
      introStart: "I'm ready",
      exit: "Exit for now",
      progress: (n, tot) => `Question ${n} of ${tot}`,
      submitting: "Analyzing your answers...",
      profileTitle: "Your profile",
      backHome: "Back to dashboard",
      previewOrganize: "In the meantime, take a peek at how the departure is organized →",
    },
    talkTo: {
      eyebrow: "One step at a time",
      title: "Who's worth talking to about it",
      intro: "Before making commitments, putting into words what you feel helps more than it might seem. You don't need someone with all the answers — you need someone who can listen without rushing.",
      peopleTitle: "Who to involve",
      people: [
        { title: "Your parents", body: "Not to ask permission, but to bring into the family what you're starting to imagine. Even if they have doubts, that's often exactly where the first honest conversation begins." },
        { title: "An older brother or sister", body: "Someone who knows you closely but isn't a parent often reads your hesitations with less pressure — and can say things you'd never say to mom and dad." },
        { title: "A trusted teacher", body: "A teacher you get along with has seen many versions of you across the years: they hold a perspective on your path nobody else does." },
        { title: "Someone who's already done an exchange", body: "It's not the same as asking someone who \"imagines what it'll be like\". Someone who was there also tells you the uncomfortable parts without sugar-coating — and that's genuinely helpful." },
      ],
      openersTitle: "How to start the conversation",
      openersHint: "These aren't sentences to read off — just examples of how to put into words something that maybe, inside you, doesn't yet have a clear shape.",
      openers: [
        "I'm thinking about it but I'm not sure. Can we talk it through a bit, just to understand what worries me?",
        "I'd like to explain what's holding me back, but first I need you to listen without answering right away. Is that ok?",
        "I took a quiz that told me I still have doubts, and it's true. Can I tell you which ones?",
        "I haven't decided anything yet. I just want to understand what I really think, and it would help to do that out loud with you.",
      ],
      backToCardsTitle: "The cards as a starting point",
      backToCardsBody: "If you'd like, re-read the \"Did you know...\" cards and bring one or two to whoever you want to talk with. They're concrete prompts — a place to start, rather than a blank page.",
      backToCardsCta: "Re-read the cards",
      exitCta: "Close",
    },
    dashboard: {
      welcome: (name) => `Hi ${name}`,
      currentPhase: "Your current phase",
      quickActions: "Quick actions",
      reminders: "Upcoming deadlines",
      noReminders: "No active deadlines. Add dates to your checklist.",
      viewLatestQuiz: "See your decision profile",
      goToPlan: "Go to Organize your departure",
      logout: "Sign out",
      checklistSummaryTitle: "Documents checklist",
      checklistSummaryCTA: "Go to checklist",
      checklistSummaryUpcoming: "Coming up soon",
      organizeSectionTitle: "Organize your departure",
      organizeDocsTitle: "Documents and deadlines",
      organizeVaxTitle: "Vaccinations",
      organizeSchoolTitle: "School program",
      organizeSchoolCta: "Read the school program",
      organizeSchoolHint: "Reflection content, no deadlines",
      checkinSectionTitle: "Check-in",
      checkinPackingTitle: "Packing list",
      checkinPackingEmpty: "Pick your climate zone to start",
      checkinDepartureTitle: "Departure documents check",
      viewAll: "See all",
      decisionProfileTitle: "Your decision profile",
      decisionProfileBody: "Review your quiz snapshot anytime, or start over if your mind has changed.",
      retakeQuiz: "Retake the quiz",
      reviewCards: "Review the cards",
      daysAgo: (n) => `${n}d ago`,
      daysLeft: (n) => `${n}d`,
      // New Bacheca copy (Upcoming deadlines moved to the top)
      remindersCompactCounter: (done, total, overdue) =>
        overdue > 0
          ? `${done} of ${total} steps completed · ${overdue} overdue`
          : `${done} of ${total} steps completed`,
      remindersOverdueBadge: "Overdue",
      departureDateMissingTitle: "Set your departure date",
      departureDateMissingBody:
        "To see upcoming deadlines, set your school year abroad in your departure profile.",
      departureDateMissingCta: "Edit profile",
      budgetTitle: "Total program budget",
      budgetSubtitle: "Fixed expenses + US one-off spending",
      budgetEmptyMessage: "Budget not calculated yet",
      budgetEmptyCta: "Start filling it in",
      budgetOpenCta: "Edit budget",
    },
    common: {
      continue: "Continue",
      back: "Back",
      language: "Language",
      save: "Save",
      cancel: "Cancel",
      loading: "Loading...",
      soon: "Coming soon",
      timingDisclaimer: "The deadlines and information you see here are indicative, designed to give you a general sense of when to act — they're neither guaranteed nor exhaustive. Every program, agency and school has its own specific timing: use the date picker to adapt them to your real path, and the deep-read content to build a fuller picture — not as a substitute for official information from your agency or school. If something is missing or doesn't match your situation, let us know — the app grows thanks to those who use it.",
      supportEmail: "exchangeplan.supporto@gmail.com",
      supportInlineCta: "Report it here",
      supportInlineFull: "Write us at exchangeplan.supporto@gmail.com",
      faqNotFoundLead: "Didn't find what you were looking for?",
      dashboardSupportLead: "Have a question or noticed something that isn't working?",
      supportWriteUs: "Write us at",
      authPromptTitle: "Save your work?",
      authPromptBody: "To save your progress and pick it up when you come back, you need an account — it only takes a few seconds. From here on it's best to continue together with a parent, who can help you handle the organizational side.",
      authPromptSignin: "Sign in with Google",
      authPromptLater: "Keep exploring",
      authReasonToggleTask: "You need an account to check off tasks and save them.",
      authReasonSetDeadline: "You need an account to set a date and save it.",
      authReasonAddCustom: "You need an account to add a custom task.",
      authReasonEditCustom: "You need an account to edit a custom task.",
      authReasonDeleteCustom: "You need an account to delete a custom task.",
      authReasonSaveProfile: "You need an account to save your departure profile.",
      authReasonSelectOption: "You need an account to select an option and save it.",
      authReasonSaveInput: "You need an account to save the value you entered.",
      authReasonSaveCheckin: "You need an account to save your Check-in choices.",
      anonBannerText: "Recommended: go through this with a parent.",
      anonBannerDismiss: "Hide",
      soonPhaseTitle: "Coming soon",
      soonPhaseBody: "We're working to make this phase available soon. In the meantime, feel free to explore the other sections that are ready.",
      soonPhaseCta: "Back to home",
    },
    pianoExcel: {
      title: "Departure plan: the free Excel file to organize your exchange in the USA",
      subtitle: "One step at a time, with deadlines calculated from your departure date.",
      bullet1: "30 steps from the initial decision to the American school",
      bullet2: "Enter your departure date and deadlines update automatically",
      bullet3: "Tick what you've done and see where you stand",
      downloadBtn: "Download the Excel file",
      fallbackLink: "If the download doesn't start, open it here",
      noticeBold: "Deadlines are indicative:",
      noticeRest: " always check dates and requirements with your agency and your school.",
      appCardTitle: "Prefer to do it online?",
      appCardBody: "In the free app you'll find the same plan with the deadlines, the steps to tick off, and everything in one place.",
      appCardCta: "Go to the free app",
    },
    footer: {
      pianoExcelLink: "Free Excel planner",
    },
  },
};
